import { AppApi, MCPServerModel } from '@/customApiClients/appApiClient/Api';
import {
    McpResponseModel,
    MgmtApi
} from '@/customApiClients/mgmtApiClient/Api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';

import { isEmbeddedSidebar } from '@/lib/embedded-sidebar';

import {
    allDataSourcesState,
    availableDataSourcesState,
    getHasAutoSelectedSources,
    getHasFetchedSources,
    selectedSourcesState,
    setHasAutoSelectedSources,
    setHasFetchedSources
} from '@/state/chat';

interface UseSourcesDataReturn {
    // Data
    dataSources: McpResponseModel[];        // Visible sources (for UI)
    selectedSources: McpResponseModel[];
    availableServersRaw: McpResponseModel[]; // All sources incl. hidden (compat alias)

    // Loading & error states
    isLoading: boolean;
    error: string | null;
    isSaving: boolean;

    // Actions
    toggleSource: (source: McpResponseModel) => void;
    fetchSources: () => Promise<void>;
}

/**
 * Custom hook for managing data sources state and API interactions.
 *
 * State layout:
 *  - allDataSourcesState       (Recoil) – ALL items incl. hidden; survives re-mounts
 *  - availableDataSourcesState (Recoil) – visible items only; used by UI
 *  - selectedSourcesState      (Recoil) – user's visible selection
 *
 * Hidden sources (isHidden=true) are always appended to the backend sync
 * payload so the agent receives them even though they are not shown in the UI.
 */
export function useSourcesData(): UseSourcesDataReturn {
    // API clients
    const mgmtApi = useMemo(
        () =>
            new MgmtApi({
                baseUrl: '/mgmt',
                baseApiParams: { credentials: 'include' }
            }),
        []
    );

    const appApi = useMemo(
        () =>
            new AppApi({
                baseApiParams: { credentials: 'include' }
            }),
        []
    );

    // Global Recoil state – both atoms survive component re-mounts
    const [allItems, setAllItems] = useRecoilState(allDataSourcesState);
    const [dataSources, setDataSources] = useRecoilState(
        availableDataSourcesState
    );
    const [selectedSources, setSelectedSources] =
        useRecoilState(selectedSourcesState);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSaving] = useState(false);

    const lastSyncedSourcesRef = useRef<string>('');

    /**
     * Fetch all available MCP servers from the management API.
     * allDataSourcesState is populated with everything (incl. hidden).
     * availableDataSourcesState is populated with visible items only.
     */
    const fetchSources = useCallback(async () => {
        // Embedded chat: the MCP attachment is fixed by the report settings and
        // resolved by the backend — no catalog is needed, and syncing here would
        // overwrite the user's standalone MCP selection.
        if (isEmbeddedSidebar) return;
        if (getHasFetchedSources() || isLoading) return;

        // Reuse globally cached data – both atoms survive re-mounts
        if (allItems.length > 0) return;

        setHasFetchedSources(true);
        setIsLoading(true);
        setError(null);

        try {
            const res = await mgmtApi.api.v1McpsList();
            const items = res?.data?.data || [];

            // Update organization ID from server response
            if (items.length > 0 && items[0].organization) {
                const serverOrg = items[0].organization;
                const currentStoredOrg = localStorage.getItem(
                    'selected-organization-id'
                );

                if (currentStoredOrg !== serverOrg) {
                    localStorage.setItem('selected-organization-id', serverOrg);

                    // Reload saved selection for the correct org
                    const storageKey = `chainlit-selected-sources-${serverOrg}`;
                    const savedSelection = localStorage.getItem(storageKey);

                    if (savedSelection) {
                        try {
                            const parsed = JSON.parse(savedSelection);
                            if (Array.isArray(parsed) && parsed.length > 0) {
                                const validSelection = parsed.filter(
                                    (saved: McpResponseModel) =>
                                        items.some(
                                            (item) => item.name === saved.name
                                        )
                                );
                                if (validSelection.length > 0) {
                                    setSelectedSources(validSelection);
                                    setHasAutoSelectedSources(true);
                                }
                            }
                        } catch (e) {
                            console.warn('Failed to parse saved selection', e);
                        }
                    }
                }
            }

            setAllItems(items);
            setDataSources(items.filter((item) => !item.isHidden));
        } catch (e: any) {
            setError(e?.message || 'Failed to load sources');
            setHasFetchedSources(false);
        } finally {
            setIsLoading(false);
        }
    }, [allItems.length, isLoading, mgmtApi, setAllItems, setDataSources, setSelectedSources]);

    /**
     * Sync selected servers to the backend.
     * Hidden sources (e.g. mcp_apps) are always appended so the agent
     * can access them even though the user never sees or toggles them.
     */
    const syncServersToBackend = useCallback(
        async (sources: McpResponseModel[]) => {
            if (allItems.length === 0) return;

            try {
                const hiddenSources = allItems.filter((s) => s.isHidden);
                const allSources = [
                    ...sources.filter((s) => !s.isHidden),
                    ...hiddenSources
                ];

                if (allSources.length === 0) return;

                const serversPayload = allSources.map((source) => {
                    const raw =
                        allItems.find((srv) => srv?.name === source.name) ||
                        source;

                    return {
                        Name: raw?.name || source.name,
                        DisplayName:
                            raw?.displayName || raw?.name || source.name,
                        Description: raw?.description || '',
                        InternalUrl: raw?.internalUrl || '',
                        ExternalUrl: raw?.externalUrl || '',
                        TransportType: raw?.transportType || '',
                        Organization: raw?.organization || '',
                        ConfigType: raw?.configType || '',
                        SourceLink: raw?.sourceLink || null
                    } as MCPServerModel;
                });

                // Validate payload – only non-hidden sources must have URLs.
                // Hidden sources may use non-HTTP URI schemes intentionally.
                const hasEmptyUrls = serversPayload.some(
                    (s) =>
                        !s.ExternalUrl &&
                        !s.InternalUrl &&
                        !allSources.find((src) => src.name === s.Name)
                            ?.isHidden
                );
                if (hasEmptyUrls) {
                    console.warn(
                        'Skipping sync: Server catalog not fully loaded'
                    );
                    return;
                }

                await appApi.mcp.setMcpServersOnUser({
                    servers: serversPayload
                });
            } catch (e: any) {
                console.error('Failed to sync servers:', e?.message);
            }
        },
        [appApi, allItems]
    );

    /**
     * Toggle a source's selection state (visible sources only).
     */
    const toggleSource = useCallback(
        (sourceModel: McpResponseModel) => {
            setSelectedSources((prev) => {
                const exists = prev.some((s) => s.name === sourceModel.name);
                return exists
                    ? prev.filter((s) => s.name !== sourceModel.name)
                    : [...prev, sourceModel];
            });
        },
        [setSelectedSources]
    );

    // Auto-select first visible source if none selected
    useEffect(() => {
        if (!dataSources.length || isLoading || getHasAutoSelectedSources())
            return;
        if (allItems.length === 0) return;

        setSelectedSources((prev) => {
            const existing = prev.filter((selectedSource) =>
                dataSources.some((s) => s.name === selectedSource.name)
            );

            if (existing.length === 0 && dataSources.length > 0) {
                const orgId =
                    localStorage.getItem('selected-organization-id') ||
                    'default';
                const storageKey = `chainlit-selected-sources-${orgId}`;
                if (localStorage.getItem(storageKey)) {
                    localStorage.removeItem(storageKey);
                }
                setHasAutoSelectedSources(true);
                return [dataSources[0]];
            }

            setHasAutoSelectedSources(true);
            return existing;
        });
    }, [dataSources, isLoading, allItems.length, setSelectedSources]);

    // Sync to backend when selection changes.
    // Also fires when selectedSources is empty but hidden sources exist
    // so hidden sources (e.g. mcp_apps) always reach the backend.
    useEffect(() => {
        if (isEmbeddedSidebar) return;
        if (allItems.length === 0) return;

        const hiddenSources = allItems.filter((s) => s.isHidden);
        if (selectedSources.length === 0 && hiddenSources.length === 0) return;

        const sourcesKey = selectedSources
            .map((s) => s.name)
            .sort()
            .join(',');
        if (sourcesKey === lastSyncedSourcesRef.current) return;

        lastSyncedSourcesRef.current = sourcesKey;
        syncServersToBackend(selectedSources);
    }, [selectedSources, allItems, syncServersToBackend]);

    return {
        dataSources,
        selectedSources,
        availableServersRaw: allItems, // alias for backwards compat
        isLoading,
        error,
        isSaving,
        toggleSource,
        fetchSources
    };
}
